<template>
<DropdownPanel ref="MenuPanel" data-component="MenuNavbar.vue" class="global-menu" Direction="bottom right">
	<template #button>
		<BaseIcon v-if="burger" name="burger" size="1.5"/>
		<template v-else>
			<BaseAvatar :user="userAvatar" size="sm" class="mr-3" data-test="avatar"/>
			<span v-html="'Profil'" data-test="profile"/>
			<BaseIcon name="chevron-down" size="0.5" class="mx-2"/>
		</template>
	</template>
	<div>
		<div>
			<template v-for="section in menu" :key="section.sectionName">
				<div class="px-4 py-3 text-base font-bold bg-neutral/5" v-html="section.sectionName"/>
				<div class="divide-y divide-neutral-100">
					<div v-for="item in section.items" :key="item.name">
						<div :class="itemClass" @click="itemClick(item)" :data-test="addTestName('global_menu',item.name)">
							<BaseIcon :name="item.icon" :set="item.iconSet ? item.iconSet : 'menu'" size="1.25" class="mr-2 text-info-50"/>
							<span v-html="item.name"/>
							<BaseIcon name="chevron-right" size="0.75" class="ml-auto text-neutral-900" :class="{'rotate-90': item.items && !item.isExpanded, '-rotate-90': item.isExpanded }"/>
						</div>
						<div v-if="item.isExpanded" class="pl-6">
							<div v-for="subItem in item.items" :key="subItem.name" :data-test="addTestName('global_menu__sub',subItem.name)" :class="itemClass" @click="itemClick(subItem)">
								<BaseIcon :name="subItem.icon" :set="item.iconSet ? item.iconSet : 'menu'" size="1.25" class="mr-2 text-info-50"/>
								<span v-html="subItem.name"/>
								<BaseIcon name="chevron-right" size="0.75" class="ml-auto text-neutral-900"/>
							</div>
						</div>
					</div>
				</div>
			</template>
		</div>
		<div v-if="ScreenSize < 1" class="h-16"/>
	</div>
</DropdownPanel>
</template>

<script lang="ts">
import { mapStores } from 'pinia';
import DropdownPanel from '../global/DropdownPanel.vue';
import { addTestName } from '../../utilities/DataTestAttribs';
import { useUserStore } from '../../../stores/user';

interface MenuNavbarItem {
	name: string;
	icon: string;
	iconSet?: string;
	url?: string;
	travelTo?: [string, any];
	isExpanded?: boolean;
	targetModule?: string;
	items?: MenuNavbarItem[];
}

/**
 * Komponent odpowiedzialny za przycisk z głównym menu nawigacyjnym.
 */
export default {
	name: 'MenuNavbar',
	components: {
		DropdownPanel,
	},
	props: {
		/**
		 * Czy pokazać przycisk w formie skróconego przycisku?
		 */
		burger: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			itemClass: 'flex items-center hover:text-info cursor-pointer px-4 py-3',
			customerMenu: [
				{
					sectionName: 'Wizytówka',
					items: [
						{
							name: 'Promuj firmę',
							// travelTo: ['CustomerProfileView', { Section: 'Promotion' }],
							icon: 'profile-promotion',
							url: '/konto/profil/promowanie',
						},
						{
							name: 'Uzupełnij wizytówkę',
							travelTo: ['CustomerProfileView', { Section: '' }],
							icon: 'profile-main',
							url: '/konto/profil/',
							isExpanded: false,
							items: [
								{
									name: 'Informacje ogólne',
									travelTo: ['CustomerProfileView', { Section: 'BasicInfo' }],
									icon: 'profile-basic-info',
									url: '/konto/profil/informacje',
								},
								{
									name: 'Kontakt',
									travelTo: ['CustomerProfileView', { Section: 'ContactInfo' }],
									icon: 'profile-contact-info',
									url: '/konto/profil/dane-kontaktowe',
								},
								{
									name: 'Opis',
									travelTo: ['CustomerProfileView', { Section: 'Description' }],
									icon: 'profile-description',
									url: '/konto/profil/opis',
								},
								{
									name: 'Zdjęcia',
									travelTo: ['CustomerProfileView', { Section: 'Gallery' }],
									icon: 'profile-photos',
									url: '/konto/profil/galeria-zdjec',
								},
								{
									name: 'Opinie',
									travelTo: ['CustomerProfileView', { Section: 'Reviews' }],
									icon: 'profile-reviews',
									url: '/konto/profil/opinie',
								},
								{
									name: 'Oferty',
									travelTo: ['CustomerProfileView', { Section: 'Offers' }],
									icon: 'profile-offers',
									url: '/konto/profil/oferty',
								},
							],
						},
					],
				},
				{
					sectionName: 'Konto',
					items: [
						{
							name: 'Powiadomienia',
							travelTo: ['CustomerSettingsView', { Section: 'Notifications' }],
							icon: 'account-notifications',
							url: '/konto/ustawienia/powiadomienia',
						},
						{
							name: 'Ustawienia konta',
							travelTo: ['CustomerSettingsView', { Section: 'ChangePassword' }],
							icon: 'account-settings',
							url: '/konto/ustawienia/haslo',
						},
						{
							name: 'Terminarz',
							travelTo: ['SchedulerPage', {}],
							icon: 'account-scheduler',
							url: '/konto/terminarz',
						},
						{
							name: 'Pomoc',
							icon: 'account-help',
							url: '/kontakt.html?idSubject=contactError',
						},
					],
				},
				{
					sectionName: 'Pozostałe',
					items: [
						{
							name: 'Certyfikaty',
							travelTo: ['CustomerProfileView', { Section: 'Certificates' }],
							icon: 'other-certificates',
							url: '/konto/profil/certyfikaty',
						},
						{
							name: 'Ranking Najlepszych',
							travelTo: ['CustomerProfileView', { Section: 'Certificates', SelectedSectionId: 'badges' }],
							icon: 'other-the-best',
							url: '/konto/profil/certyfikaty/odznaki',
						},
						{
							name: 'Program Partnerski',
							travelTo: ['CustomerProfileView', { Section: 'Partnership' }],
							icon: 'other-partnership',
							url: '/konto/profil/program-partnerski',
						},
					],
				},
			],
			adminMenu: [
				{
					sectionName: 'Administracja',
					items: [
						{
							name: 'Obsługa bieżąca',
							icon: '',
							url: '/admin/WFObjectList.ofer',
						},
						{
							name: 'Rejestracja dostawcy',
							icon: '',
							url: '/rejestracja/wybierz-klientow',
						},
					],
				},
			],
		};
	},
	computed: {
		...mapStores(useUserStore),
		menu() {
			const result = [];

			const currentModule = this.$root.Module;
			const role = this.userStore.role;

			if (role === 'BUYER') {
				result.push(this.generateBuyerMenu());
			} else {
				result.push(...this.customerMenu);
			}

			if (role === 'ADMIN') {
				result.push(...this.adminMenu);
			}

			result.push(this.generateFooterMenu(role, currentModule));

			return result;
		},
		userAvatar() {
			return {
				Avatar: this.userStore.avatar,
				FirstName: this.userStore.firstName,
				LastName: this.userStore.lastName,
			};
		},
	},
	methods: {
		addTestName,
		/**
		 * Przenosi użytkownika na wskazaną stronę.
		 */
		itemClick(item:MenuNavbarItem) {
			// Jeżeli element ma podelementy, to je rozwiń.
			if (item.items) {
				item.isExpanded = !item.isExpanded;
				return;
			}

			const currentModule = this.$root.Module;
			const targetModule = item.targetModule || 'CustomerView';
			(this.$refs.MenuPanel as typeof DropdownPanel).Toggle();

			// Jeżeli jest to element wylogowywujący, to to zrób.
			if (item.travelTo && item.travelTo[0] === 'Logout') {
				this.$root.Logout();
				return;
			}

			// Jeżeli element nie prowadzi na żadną ścieżkę lub prowadzi
			// na inny moduł, to przejdź pod wskazany URL.
			if (item.url && (!item.travelTo || currentModule !== targetModule)) {
				document.location.href = item.url;
				return;
			}

			this.State.Travel(...item.travelTo);
		},

		generateFooterMenu(role:string, currentModule:string) {
			const footer = {
				sectionName: '&nbsp;',
				items: [
					{
						name: 'Strona główna Oferteo',
						icon: 'main-home',
						url: '/',
					},
				] as MenuNavbarItem[],
			};

			if (role !== 'BUYER' && currentModule !== 'BuyerView') {
				footer.items.push({
					name: 'Przełącz na profil zleceniodawcy',
					icon: 'main-switch',
					url: '/konto/moje-zapytania',
				});
			}

			footer.items.push({
				name: 'Wyloguj',
				icon: 'main-logout',
				travelTo: ['Logout', {}],
			});

			return footer;
		},

		generateBuyerMenu() {
			const buyerMenu = {
				sectionName: '&nbsp;',
				items: [
					{
						name: 'Moje zapytania',
						icon: 'done-check-notes',
						iconSet: 'base',
						url: '/konto/moje-zapytania',
					},
				] as MenuNavbarItem[],
			};

			const isLogged = this.userStore.userId > 0;
			if (isLogged) {
				buyerMenu.items.push({
					name: 'Dane kontaktowe',
					icon: 'user-circle-new',
					iconSet: 'base',
					url: '/rfp/dane-kontaktowe.ofer',
				});
			}

			const isCustomer = this.userStore.isCustomer;
			buyerMenu.items.push(isCustomer ? {
				name: 'Zaloguj się jako Specjalista',
				icon: 'main-switch',
				url: '/logowanie',
			} : {
				name: 'Dołącz do Specjalistów',
				icon: 'main-switch',
				url: '/rejestracja/wybierz-klientow',
			});

			buyerMenu.items.push({
				name: 'Pomoc',
				icon: 'account-help',
				url: '/kontakt.html?idSubject=contactError',
			});

			return buyerMenu;
		},
	},
};

</script>

<style lang="scss">
.global-menu .dropdown-panel {
	padding: 0;
	overflow-y: auto;
}

.global-navigation .global-menu .dropdown-panel {
	width: 22.5rem;
	top: 3rem;
	max-height: calc(100vh - 6rem);
}

.page-header .dropdown-wrapper {
	display: flex;
}

.page-header .global-menu .dropdown-panel {
	position: fixed;
	left: 0;
	right: 0;
	top: 4rem;
	width: 100vw;
	margin-right: 0;
	max-height: calc(100vh - 7rem);
}
</style>
